import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import {
  Cpu, Plus, Trash, ShieldStar, Check, Warning, Disc, Package,
  ArrowSquareOut, FloppyDisk, Bug, UploadSimple, Star
} from "@phosphor-icons/react";

const DAW_OPTIONS = ["FL Studio", "Ableton Live", "Logic Pro", "Pro Tools", "Studio One", "Bitwig", "Cubase", "Reaper"];

export default function AdminPlugin() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "updates";
  const setTab = (k) => setParams({ tab: k });
  return (
    <div className="p-6 lg:p-8" data-testid="admin-plugin">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-brand-red mb-2">◢ PLUGIN CONTROL CENTER</div>
          <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.9]">
            SHIP <span className="text-brand-red neon-red">FIRMWARE.</span>
          </h1>
        </div>
      </div>

      <div className="flex gap-px bg-zinc-900 border border-zinc-900 mb-6 flex-wrap" data-testid="plugin-tabs">
        {[
          ["builds", "BINARY BUILDS", UploadSimple],
          ["updates", "RELEASE NOTES", Cpu],
          ["packs", "SOUND PACKS", Package],
          ["crashes", "CRASH REPORTS", Bug],
          ["compat", "COMPATIBILITY", ShieldStar],
        ].map(([k, label, Icon]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`plugin-tab-${k}`}
            className={`flex-1 min-w-[160px] px-4 py-3 text-[10px] tracking-[0.3em] uppercase flex items-center justify-center gap-2 transition-colors ${
              tab === k ? "bg-brand-red text-black" : "bg-bg-surface text-ink-mute hover:text-white"
            }`}
          >
            <Icon size={12} weight="bold" />
            {label}
          </button>
        ))}
      </div>

      {tab === "builds" && <BinaryBuilds />}
      {tab === "updates" && <VstUpdates />}
      {tab === "packs" && <SoundPacks />}
      {tab === "crashes" && <CrashReports />}
      {tab === "compat" && <Compatibility />}
    </div>
  );
}

// ─────── BINARY BUILDS (drag-drop VST3 / AU / VST2 upload) ───────
function BinaryBuilds() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    platform: "macos", artifact: "vst3", version: "1.1.0", notes: "", setLatest: true,
  });
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(0);

  const load = async () => {
    const { data } = await api.get("/admin/plugin/builds");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const artifactOptions = form.platform === "windows" ? ["vst3", "vst2"] : ["vst3", "au"];

  const upload = async (e) => {
    e?.preventDefault();
    setErr("");
    if (!file) { setErr("Pick a file first"); return; }
    if (!form.version.trim()) { setErr("Version is required"); return; }
    setBusy(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("platform", form.platform);
      fd.append("artifact", form.artifact);
      fd.append("version", form.version.trim());
      fd.append("notes", form.notes || "");
      fd.append("set_latest", form.setLatest ? "true" : "false");
      fd.append("file", file);
      await api.post("/admin/plugin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setFile(null);
      setForm((f) => ({ ...f, notes: "" }));
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const setLatest = async (id) => {
    await api.patch(`/admin/plugin/builds/${id}/set-latest`);
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this build artifact?")) return;
    await api.delete(`/admin/plugin/builds/${id}`);
    load();
  };

  const dropHandler = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="builds-panel">
      <div className="border border-zinc-800 bg-bg-surface grain p-6 lg:col-span-1">
        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-4 flex items-center gap-2">
          <UploadSimple size={12} weight="bold" /> UPLOAD BINARY
        </div>

        <form onSubmit={upload} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <LField label="PLATFORM">
              <select
                value={form.platform}
                onChange={(e) => {
                  const platform = e.target.value;
                  setForm((f) => ({
                    ...f,
                    platform,
                    artifact: platform === "windows" ? "vst3" : "vst3",
                  }));
                }}
                data-testid="build-platform"
                className="w-full bg-black border border-zinc-800 text-xs uppercase tracking-[0.2em] px-3 py-2 focus:border-brand-red focus:outline-none"
              >
                <option value="macos" label="MACOS" />
                <option value="windows" label="WINDOWS" />
              </select>
            </LField>
            <LField label="ARTIFACT">
              <select
                value={form.artifact}
                onChange={(e) => setForm((f) => ({ ...f, artifact: e.target.value }))}
                data-testid="build-artifact"
                className="w-full bg-black border border-zinc-800 text-xs uppercase tracking-[0.2em] px-3 py-2 focus:border-brand-red focus:outline-none"
              >
                {artifactOptions.map((o) => (
                  <option key={o} value={o} label={o.toUpperCase()} />
                ))}
              </select>
            </LField>
          </div>

          <LField label="VERSION">
            <input
              value={form.version}
              onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))}
              placeholder="1.1.0"
              data-testid="build-version"
              className="w-full bg-black border border-zinc-800 text-xs uppercase tracking-[0.2em] px-3 py-2 focus:border-brand-red focus:outline-none"
            />
          </LField>

          <LField label="RELEASE NOTES">
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="• Fixed denormal in saturation stage&#10;• AU validator passes"
              rows={3}
              data-testid="build-notes"
              className="w-full bg-black border border-zinc-800 text-xs px-3 py-2 focus:border-brand-red focus:outline-none resize-none font-mono"
            />
          </LField>

          <label
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={dropHandler}
            data-testid="build-drop"
            className={`block border border-dashed cursor-pointer text-center p-6 transition-colors ${
              dragOver ? "border-brand-red bg-brand-red/5" : "border-zinc-700 hover:border-zinc-500"
            }`}
          >
            <input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="build-file"
            />
            <UploadSimple size={24} weight="bold" className="mx-auto text-brand-red mb-2" />
            {file ? (
              <div>
                <div className="text-xs font-mono text-white truncate">{file.name}</div>
                <div className="text-[10px] text-ink-dim mt-1">{(file.size / (1024 * 1024)).toFixed(2)} MB</div>
              </div>
            ) : (
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-ink-mute">DRAG & DROP</div>
                <div className="text-[10px] text-ink-dim mt-1">or click to browse</div>
              </div>
            )}
          </label>

          <label className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase cursor-pointer">
            <input
              type="checkbox"
              checked={form.setLatest}
              onChange={(e) => setForm((f) => ({ ...f, setLatest: e.target.checked }))}
              data-testid="build-set-latest"
              className="accent-brand-red"
            />
            <span>SET AS LATEST</span>
          </label>

          {err && (
            <div className="text-[10px] text-brand-red border border-brand-red/40 bg-brand-red/5 p-2" data-testid="build-error">
              {err}
            </div>
          )}

          {busy && progress > 0 && (
            <div className="border border-zinc-800 h-1.5">
              <div className="bg-brand-red h-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !file}
            data-testid="build-submit"
            className="w-full bg-brand-red text-black text-xs font-bold tracking-[0.3em] uppercase py-3 hover:bg-white disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
          >
            <UploadSimple size={12} weight="bold" />
            {busy ? `UPLOADING ${progress}%` : "UPLOAD & DEPLOY"}
          </button>
        </form>

        <div className="mt-6 pt-4 border-t border-zinc-900 text-[10px] text-ink-dim leading-relaxed">
          <p className="text-brand-red mb-1.5 tracking-[0.25em] uppercase">▸ How this works</p>
          Uploaded VST3 / AU / VST2 binaries replace the placeholder stubs inside the per-user signed .zip downloads. macOS bundles get the right plist + executable bit automatically. Mark a build "LATEST" to start serving it immediately — no restart needed.
        </div>
      </div>

      <div className="border border-zinc-800 bg-bg-surface grain p-6 lg:col-span-2 overflow-x-auto">
        <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim mb-3">
          UPLOADED BUILDS · {items.length}
        </div>
        {items.length === 0 ? (
          <div className="text-center py-12 text-ink-dim text-[10px] tracking-[0.3em] uppercase">
            NO BUILDS YET — UPLOAD A VST3 / AU / VST2 BINARY ON THE LEFT.<br />
            UNTIL THEN, USERS WILL DOWNLOAD THE PLACEHOLDER STUB (INSTALLS CORRECTLY BUT WON'T MAKE SOUND).
          </div>
        ) : (
          <table className="w-full text-xs" data-testid="builds-table">
            <thead className="text-[10px] tracking-[0.25em] uppercase text-ink-dim border-b border-zinc-900">
              <tr>
                <th className="text-left py-2">PLATFORM · ARTIFACT</th>
                <th className="text-left py-2">VERSION</th>
                <th className="text-left py-2">SIZE</th>
                <th className="text-left py-2">SHA-256</th>
                <th className="text-left py-2">UPLOADED</th>
                <th className="text-right py-2"></th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {items.map((b) => (
                <tr key={b.id} className="border-b border-zinc-900 hover:bg-black/30" data-testid={`build-${b.id}`}>
                  <td className="py-2.5">
                    <span className={`px-1.5 py-0.5 text-[9px] tracking-[0.25em] uppercase border ${
                      b.platform === "macos" ? "border-zinc-400 text-zinc-200" : "border-brand-cyan text-brand-cyan"
                    }`}>
                      {b.platform}
                    </span>
                    <span className="ml-2 text-brand-red font-bold uppercase">{b.artifact}</span>
                  </td>
                  <td className="py-2.5 text-white">{b.version}</td>
                  <td className="py-2.5 text-ink-mute">{(b.size_bytes / (1024 * 1024)).toFixed(2)} MB</td>
                  <td className="py-2.5 text-ink-mute truncate max-w-[140px]" title={b.sha256}>
                    {b.sha256.slice(0, 12)}…
                  </td>
                  <td className="py-2.5 text-ink-mute text-[10px]">
                    {new Date(b.uploaded_at).toLocaleDateString()} <span className="text-ink-dim">{b.uploaded_by}</span>
                  </td>
                  <td className="py-2.5 text-right">
                    {b.is_latest ? (
                      <span className="inline-flex items-center gap-1 text-[9px] tracking-[0.25em] uppercase text-brand-red border border-brand-red px-2 py-1">
                        <Star size={10} weight="fill" /> LATEST
                      </span>
                    ) : (
                      <button
                        onClick={() => setLatest(b.id)}
                        data-testid={`build-set-latest-${b.id}`}
                        className="text-[9px] tracking-[0.25em] uppercase border border-zinc-700 px-2 py-1 hover:border-brand-red hover:text-brand-red"
                      >
                        SET LATEST
                      </button>
                    )}
                    <button
                      onClick={() => del(b.id)}
                      data-testid={`build-delete-${b.id}`}
                      className="ml-2 text-ink-mute hover:text-brand-red p-1"
                      title="Delete"
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function LField({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.3em] uppercase text-ink-dim mb-1.5">{label}</span>
      {children}
    </label>
  );
}

// ─────── VST UPDATES ───────
function VstUpdates() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    version: "", channel: "stable", platform: "both", notes: "",
    daw_compat: ["FL Studio"], file_url: ""
  });

  const load = async () => {
    const { data } = await api.get("/admin/vst-updates");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/vst-updates", form);
      setForm({ version: "", channel: "stable", platform: "both", notes: "", daw_compat: ["FL Studio"], file_url: "" });
      load();
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this update?")) return;
    await api.delete(`/admin/vst-updates/${id}`);
    load();
  };

  const toggleDaw = (daw) => {
    setForm((f) => ({
      ...f,
      daw_compat: f.daw_compat.includes(daw)
        ? f.daw_compat.filter((d) => d !== daw)
        : [...f.daw_compat, daw]
    }));
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Push update form */}
      <form onSubmit={submit} className="border border-zinc-800 bg-bg-surface grain p-5 lg:col-span-1" data-testid="vst-update-form">
        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-4 flex items-center gap-2">
          <Plus size={12} weight="bold" /> PUSH NEW UPDATE
        </div>
        <div className="space-y-3">
          <Field label="Version" value={form.version} onChange={(v) => setForm({ ...form, version: v })} placeholder="1.0.5" testid="vst-version" required />
          <div className="grid grid-cols-2 gap-2">
            <SelectField label="Channel" value={form.channel} onChange={(v) => setForm({ ...form, channel: v })} options={["stable", "beta"]} testid="vst-channel" />
            <SelectField label="Platform" value={form.platform} onChange={(v) => setForm({ ...form, platform: v })} options={["both", "win", "mac"]} testid="vst-platform" />
          </div>
          <Field label="Release notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} placeholder="Bug fixes, perf, new feature…" testid="vst-notes" multiline />
          <Field label="File URL (optional)" value={form.file_url} onChange={(v) => setForm({ ...form, file_url: v })} placeholder="https://cdn.../beatlab_1.0.5.vst3" testid="vst-file-url" />
          <div>
            <span className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">DAW compatibility</span>
            <div className="grid grid-cols-2 gap-1 mt-1.5">
              {DAW_OPTIONS.map((d) => (
                <button
                  type="button"
                  key={d}
                  onClick={() => toggleDaw(d)}
                  data-testid={`vst-daw-${d.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`text-[9px] tracking-[0.2em] uppercase px-2 py-1.5 border transition-colors ${
                    form.daw_compat.includes(d)
                      ? "border-brand-red text-brand-red bg-brand-red/10"
                      : "border-zinc-800 text-ink-mute hover:border-zinc-600"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        </div>
        <button type="submit" disabled={busy || !form.version} data-testid="vst-submit" className="mt-5 w-full bg-brand-red text-black text-[10px] tracking-[0.3em] uppercase font-bold py-3 hover:bg-white disabled:opacity-50">
          {busy ? "PUSHING…" : "▸ SHIP UPDATE"}
        </button>
      </form>

      {/* Update history */}
      <div className="border border-zinc-800 bg-bg-surface grain p-5 lg:col-span-2" data-testid="vst-update-list">
        <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim mb-4 flex items-center justify-between">
          <span className="flex items-center gap-2"><Disc size={11} weight="bold" className="text-brand-red" /> RELEASE HISTORY · {items.length}</span>
          <span className="text-brand-green animate-flicker">● LIVE</span>
        </div>
        {items.length === 0 ? (
          <div className="text-xs text-ink-dim py-10 text-center">No updates pushed yet.</div>
        ) : (
          <ul className="space-y-3">
            {items.map((u) => (
              <li key={u.id} className="border border-zinc-900 p-4 bg-black/40 hover:border-zinc-700 transition-colors" data-testid={`vst-update-${u.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-1.5 self-stretch ${u.channel === "stable" ? "bg-brand-green" : "bg-brand-purple"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span className="font-display font-black text-2xl tracking-tighter">{u.version}</span>
                      <span className={`text-[9px] tracking-[0.3em] uppercase px-1.5 py-0.5 border ${
                        u.channel === "stable" ? "border-brand-green text-brand-green" : "border-brand-purple text-brand-purple"
                      }`}>{u.channel}</span>
                      <span className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">{u.platform.toUpperCase()}</span>
                      <span className="text-[9px] tracking-[0.3em] uppercase text-ink-dim ml-auto">{new Date(u.released_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-xs text-ink-mute mt-2 leading-relaxed">{u.notes}</p>
                    <div className="flex flex-wrap gap-1 mt-3">
                      {(u.daw_compat || []).map((d) => (
                        <span key={d} className="text-[9px] tracking-[0.2em] uppercase border border-zinc-800 text-ink-mute px-1.5 py-0.5">{d}</span>
                      ))}
                    </div>
                    {u.downloads != null && (
                      <div className="text-[9px] tracking-[0.3em] uppercase text-ink-dim mt-3 flex items-center gap-3">
                        <span>↓ {u.downloads.toLocaleString()} downloads</span>
                        <span>by {u.released_by}</span>
                      </div>
                    )}
                  </div>
                  <button onClick={() => remove(u.id)} data-testid={`vst-delete-${u.id}`} className="text-ink-mute hover:text-brand-red p-2">
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─────── SOUND PACKS ───────
function SoundPacks() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", category: "808", preset_count: 16, cover_color: "#FF003C", is_beta: false });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/sound-packs");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/sound-packs", { ...form, preset_count: parseInt(form.preset_count, 10) });
      setForm({ name: "", category: "808", preset_count: 16, cover_color: "#FF003C", is_beta: false });
      load();
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this sound pack?")) return;
    await api.delete(`/admin/sound-packs/${id}`);
    load();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={submit} className="border border-zinc-800 bg-bg-surface grain p-5 lg:col-span-1" data-testid="pack-form">
        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-4 flex items-center gap-2">
          <Plus size={12} weight="bold" /> NEW SOUND PACK
        </div>
        <div className="space-y-3">
          <Field label="Pack name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="VOID 808 VOL.2" testid="pack-name" required />
          <SelectField label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={["808", "lead", "pad", "drums", "vox", "sfx"]} testid="pack-category" />
          <Field label="Preset count" value={form.preset_count} onChange={(v) => setForm({ ...form, preset_count: v })} type="number" testid="pack-count" />
          <div>
            <span className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">Cover color</span>
            <div className="flex gap-1.5 mt-1.5">
              {["#FF003C", "#8B00FF", "#FF6B00", "#00FFAA", "#FF00C8", "#00C2FF"].map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => setForm({ ...form, cover_color: c })}
                  data-testid={`pack-color-${c.slice(1)}`}
                  className="w-8 h-8 border"
                  style={{ background: c, borderColor: form.cover_color === c ? "#fff" : "#27272A" }}
                />
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-ink-mute cursor-pointer">
            <input type="checkbox" checked={form.is_beta} onChange={(e) => setForm({ ...form, is_beta: e.target.checked })} data-testid="pack-beta" className="accent-brand-red" />
            BETA / EARLY ACCESS
          </label>
        </div>
        <button type="submit" disabled={busy || !form.name} data-testid="pack-submit" className="mt-5 w-full bg-brand-red text-black text-[10px] tracking-[0.3em] uppercase font-bold py-3 hover:bg-white disabled:opacity-50">
          {busy ? "..." : "▸ PUBLISH PACK"}
        </button>
      </form>

      <div className="lg:col-span-2">
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900" data-testid="pack-grid">
          {items.length === 0 ? (
            <div className="bg-bg-surface p-10 text-center text-xs text-ink-dim col-span-full">No sound packs yet.</div>
          ) : items.map((p) => (
            <div key={p.id} className="bg-bg-surface relative overflow-hidden group" data-testid={`pack-${p.id}`}>
              <div className="h-24 relative" style={{ background: `linear-gradient(135deg, ${p.cover_color}, #050505)` }}>
                <div className="absolute inset-0 scanlines opacity-50" />
                {p.is_beta && (
                  <span className="absolute top-2 right-2 text-[9px] tracking-[0.3em] uppercase bg-black/80 text-brand-purple border border-brand-purple px-1.5 py-0.5">
                    BETA
                  </span>
                )}
              </div>
              <div className="p-4">
                <div className="font-display font-black tracking-tighter text-lg truncate">{p.name}</div>
                <div className="flex items-center justify-between text-[10px] tracking-[0.25em] uppercase text-ink-dim mt-2">
                  <span>{p.category.toUpperCase()} · {p.preset_count} presets</span>
                  <button onClick={() => remove(p.id)} data-testid={`pack-delete-${p.id}`} className="text-ink-dim hover:text-brand-red">
                    <Trash size={12} weight="bold" />
                  </button>
                </div>
                {p.downloads != null && (
                  <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mt-1">↓ {p.downloads.toLocaleString()} dl</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────── CRASH REPORTS ───────
function CrashReports() {
  const [items, setItems] = useState([]);
  const load = async () => {
    const { data } = await api.get("/admin/crash-reports");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const resolve = async (id) => {
    await api.patch(`/admin/crash-reports/${id}/resolve`);
    load();
  };

  const open = items.filter((c) => !c.resolved).length;

  return (
    <div className="border border-zinc-800 bg-bg-surface grain p-5" data-testid="crash-reports">
      <div className="flex items-center justify-between mb-5">
        <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim flex items-center gap-2">
          <Bug size={12} weight="fill" className="text-brand-red" /> CRASH REPORTS · {items.length}
        </div>
        <span className="text-[10px] tracking-[0.3em] uppercase text-brand-red">{open} OPEN</span>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-ink-dim py-10 text-center">No crash reports — systems clean ✓</div>
      ) : (
        <ul className="space-y-2">
          {items.map((c) => (
            <li key={c.id} className={`border p-4 ${c.resolved ? "border-zinc-900 bg-black/40" : "border-brand-red/40 bg-brand-red/5"}`} data-testid={`crash-${c.id}`}>
              <div className="flex items-start gap-3">
                <Warning size={20} weight="fill" className={c.resolved ? "text-zinc-700" : "text-brand-red animate-flicker"} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-3 flex-wrap text-[10px] tracking-[0.3em] uppercase">
                    <span className="text-white">{c.daw}</span>
                    <span className="text-ink-dim">v{c.plugin_version}</span>
                    <span className="text-ink-dim ml-auto">{new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-white mt-2 font-mono break-all">{c.error}</p>
                  {c.user_email && (
                    <div className="text-[10px] tracking-[0.25em] uppercase text-ink-dim mt-2">USER: {c.user_email}</div>
                  )}
                </div>
                {!c.resolved && (
                  <button onClick={() => resolve(c.id)} data-testid={`crash-resolve-${c.id}`} className="bg-brand-green text-black text-[10px] tracking-[0.3em] uppercase font-bold px-3 py-2 hover:bg-white whitespace-nowrap">
                    <Check size={12} weight="bold" className="inline mr-1" /> RESOLVE
                  </button>
                )}
                {c.resolved && (
                  <span className="text-[10px] tracking-[0.3em] uppercase text-brand-green flex items-center gap-1">
                    <Check size={12} weight="bold" /> RESOLVED
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─────── COMPATIBILITY ───────
function Compatibility() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/compatibility").then(({ data }) => setItems(data));
  }, []);

  return (
    <div className="border border-zinc-800 bg-bg-surface grain p-5" data-testid="compatibility-table">
      <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim mb-5 flex items-center gap-2">
        <ShieldStar size={11} weight="bold" className="text-brand-red" /> DAW COMPATIBILITY MATRIX
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-zinc-900 text-[10px] tracking-[0.3em] uppercase text-ink-dim">
              <th className="text-left py-2.5 pr-4">DAW</th>
              <th className="text-left py-2.5 pr-4">VERSION</th>
              <th className="text-left py-2.5 pr-4">STATUS</th>
              <th className="text-center py-2.5 pr-2">VST3</th>
              <th className="text-center py-2.5 pr-2">VST2</th>
              <th className="text-center py-2.5 pr-4">AU</th>
              <th className="text-left py-2.5">NOTES</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row, i) => (
              <tr key={i} className="border-b border-zinc-900" data-testid={`compat-row-${i}`}>
                <td className="py-3 pr-4 font-display font-bold uppercase">{row.daw}</td>
                <td className="py-3 pr-4 text-ink-mute font-mono">{row.version}</td>
                <td className="py-3 pr-4">
                  <span className={`text-[10px] tracking-[0.25em] uppercase px-2 py-1 border ${
                    row.status === "fully_supported" ? "border-brand-green text-brand-green"
                    : row.status === "beta" ? "border-brand-purple text-brand-purple"
                    : "border-zinc-700 text-ink-mute"
                  }`}>
                    {row.status.replace("_", " ")}
                  </span>
                </td>
                <td className="text-center text-lg">{row.vst3 ? <Check className="inline text-brand-green" /> : <span className="text-ink-dim">—</span>}</td>
                <td className="text-center text-lg">{row.vst2 ? <Check className="inline text-brand-green" /> : <span className="text-ink-dim">—</span>}</td>
                <td className="text-center text-lg pr-4">{row.au ? <Check className="inline text-brand-green" /> : <span className="text-ink-dim">—</span>}</td>
                <td className="py-3 text-ink-mute text-xs">{row.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────── SHARED ───────
function Field({ label, value, onChange, type = "text", placeholder, testid, multiline = false, ...rest }) {
  const Cmp = multiline ? "textarea" : "input";
  return (
    <label className="block">
      <span className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">{label}</span>
      <Cmp
        type={multiline ? undefined : type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        rows={multiline ? 3 : undefined}
        {...rest}
        className="mt-1 w-full bg-black border border-zinc-800 px-2 py-1.5 text-sm font-mono outline-none focus:border-brand-red resize-none"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full bg-black border border-zinc-800 px-2 py-1.5 text-sm font-mono outline-none focus:border-brand-red appearance-none"
      >
        {options.map((o) => <option key={o} value={o} label={o.toUpperCase()} />)}
      </select>
    </label>
  );
}
